import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Aml.css';
import AmlNav from './AmlNav';
import axiosInstance from '../api';
import useAmlFramework from './useAmlFramework';
import { canManageAml } from './amlPermissions';
import {
  getComplianceSummary, getAmlOverview, saveAmlGovernance,
  listAmlTraining, createAmlTraining,
} from './amlApi';

/** AML Program Overview (wireframe 2.1). Test pass-rate and framework name come from the same
 *  /audit/compliance-summary endpoint AuditPage.js already reads from — this page never
 *  re-derives that number, it just shows it for the AML framework specifically. */
export default function AmlOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = canManageAml();
  const { status, frameworks, frameworkId, framework, setFrameworkId } = useAmlFramework();
  const [posture, setPosture] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showGovEdit, setShowGovEdit] = useState(false);
  const [govForm, setGovForm] = useState({
    complianceOfficerName: '', complianceOfficerEmail: '', appointmentDate: '', regulatorRegistration: '',
    independenceCheck: false, committeeMemberCount: 0,
    riskMatrixHighThreshold: 15, riskMatrixMediumThreshold: 8,
  });
  const [govSaving, setGovSaving] = useState(false);
  const [govError, setGovError] = useState('');
  const [training, setTraining] = useState([]);
  const [showTrainingAdd, setShowTrainingAdd] = useState(false);
  const [trainingForm, setTrainingForm] = useState({ courseName: '', audienceRole: '', period: '', completionPct: '', autoComputed: false });
  const [trainingSaving, setTrainingSaving] = useState(false);
  const [trainingError, setTrainingError] = useState('');
  const [reportGenerating, setReportGenerating] = useState(false);

  const loadTraining = (fwId) => listAmlTraining(fwId).then(setTraining).catch(() => {});

  useEffect(() => {
    if (status !== 'ok' || !frameworkId) return;
    let alive = true;
    (async () => {
      const [summaryList, overview] = await Promise.all([
        getComplianceSummary().catch(() => []),
        getAmlOverview(frameworkId).catch(() => null),
      ]);
      if (!alive) return;
      setPosture((summaryList || []).find((p) => p.frameworkName === framework?.displayName) || null);
      setSummary(overview);
      loadTraining(frameworkId);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, frameworkId]);

  const submitTraining = async () => {
    if (!trainingForm.courseName.trim()) { setTrainingError(t('Aml.courseNameRequired', 'A course name is required.')); return; }
    const pct = trainingForm.autoComputed || trainingForm.completionPct === '' ? null : Number(trainingForm.completionPct);
    if (pct != null && !(pct >= 0 && pct <= 100)) {
      setTrainingError(t('Aml.completionRange', 'Completion must be between 0 and 100 percent.'));
      return;
    }
    setTrainingSaving(true);
    setTrainingError('');
    try {
      await createAmlTraining(frameworkId, { ...trainingForm, completionPct: pct });
      setShowTrainingAdd(false);
      setTrainingForm({ courseName: '', audienceRole: '', period: '', completionPct: '', autoComputed: false });
      loadTraining(frameworkId);
    } catch (e) {
      setTrainingError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the course.'));
    } finally {
      setTrainingSaving(false);
    }
  };

  // FR-11: generates the AML committee report from the same live aggregates shown above, then
  // opens it in Executive Reports (Dashboard > Reports) for the compliance officer to review and
  // finalise.
  const generateBoardReport = async () => {
    setReportGenerating(true);
    try {
      const res = await axiosInstance.post('/reports', { templateId: 'AML_BOARD_SUMMARY', frameworkId });
      navigate(res?.data?.id ? `/dashboard/reports?open=${res.data.id}` : '/dashboard/reports');
    } finally {
      setReportGenerating(false);
    }
  };

  const openGovEdit = (gov) => {
    setGovForm({
      complianceOfficerName: gov?.complianceOfficerName || '',
      complianceOfficerEmail: gov?.complianceOfficerEmail || '',
      appointmentDate: gov?.appointmentDate || '',
      regulatorRegistration: gov?.regulatorRegistration || '',
      independenceCheck: !!gov?.independenceCheck,
      committeeMemberCount: gov?.committeeMemberCount || 0,
      riskMatrixHighThreshold: gov?.riskMatrixHighThreshold ?? 15,
      riskMatrixMediumThreshold: gov?.riskMatrixMediumThreshold ?? 8,
    });
    setGovError('');
    setShowGovEdit(true);
  };

  const saveGovernance = async () => {
    setGovSaving(true);
    setGovError('');
    try {
      await saveAmlGovernance(frameworkId, govForm);
      setShowGovEdit(false);
      const overview = await getAmlOverview(frameworkId).catch(() => null);
      setSummary(overview);
    } catch (e) {
      setGovError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save governance.'));
    } finally {
      setGovSaving(false);
    }
  };

  if (status === 'loading') return <div className="aml-page"><div className="aml-spinner" /></div>;

  if (status === 'error') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.loadError', 'Could not load the AML Program. Your session may have expired — try reloading.')}
        </div>
      </div>
    );
  }

  if (status === 'not-enrolled') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.notEnrolled', 'Your organization has not enrolled in an AML framework yet. Enroll in SEPS-IGT-2025-0113 or another AML framework from the Frameworks page to activate the AML Program.')}
          <div style={{ marginTop: '0.75rem' }}>
            <button className="aml-btn aml-btn-primary" onClick={() => navigate('/dashboard/compliance/frameworks')}>
              {t('Aml.goToFrameworks', 'Go to Frameworks')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const gov = summary?.governance;
  // One source for training figures: the courses logged in the Training section. The governance
  // card shows their average instead of a second number typed by hand.
  const coursesWithPct = training.filter((r) => r.completionPct != null);
  const trainingAverage = coursesWithPct.length
    ? Math.round(coursesWithPct.reduce((sum, r) => sum + Number(r.completionPct), 0) / coursesWithPct.length)
    : null;
  const focusItems = [
    summary && summary.overdueReviewCount > 0 && {
      severity: 'HIGH', label: t('Aml.focusEdd', '{{count}} due-diligence reviews overdue across customer segments', { count: summary.overdueReviewCount }),
      tag: t('Aml.focusTagSegment', 'CUSTOMER SEGMENT'), to: '/dashboard/aml/segments',
    },
    summary && summary.filingsDueIn30Days > 0 && {
      severity: 'MEDIUM', label: t('Aml.focusFilings', '{{count}} filings due within 30 days', { count: summary.filingsDueIn30Days }),
      tag: t('Aml.focusTagFiling', 'AML FILING'), to: '/dashboard/aml/filings',
    },
    summary && summary.highRiskCount > 0 && {
      severity: 'MEDIUM', label: t('Aml.focusRisk', '{{count}} high-residual risks on the assessment', { count: summary.highRiskCount }),
      tag: t('Aml.focusTagRisk', 'RISK ASSESSMENT'), to: '/dashboard/aml/risk-assessment',
    },
  ].filter(Boolean);

  return (
    <div className="aml-page">
      <div className="aml-header">
        <div>
          <h1 className="aml-title">{t('Aml.title', 'AML Program')}</h1>
          <p className="aml-subtitle">
            {t('Aml.subtitle', 'Money-laundering prevention program for {{framework}}.', { framework: framework.displayName })}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <span className="aml-sample-chip">{t('Aml.sampleData', 'Live data')}</span>
          {canManage && (
            <button className="aml-btn" disabled={reportGenerating} onClick={generateBoardReport}>
              {reportGenerating ? t('Aml.generating', 'Generating…') : t('Aml.generateBoardReport', 'Generate board report')}
            </button>
          )}
        </div>
      </div>

      <div className="aml-stat-bar">
        <div className="aml-stat">
          <div className="aml-stat-label">{t('Aml.statTestsPassing', 'TESTS PASSING')}</div>
          <div className="aml-stat-val">
            {posture ? posture.passedTests : '—'}
            {posture && <span style={{ fontSize: '0.8125rem', fontWeight: 400, color: '#64748b' }}> {t('Aml.of', 'of')} {posture.totalTests}</span>}
          </div>
        </div>
        <div className="aml-stat">
          <div className="aml-stat-label">{t('Aml.statFilingsDue', 'FILINGS DUE (30 DAYS)')}</div>
          <div className="aml-stat-val">{summary ? summary.filingsDueIn30Days : '—'}</div>
        </div>
        <div className="aml-stat">
          <div className="aml-stat-label">{t('Aml.statSegments', 'CUSTOMER SEGMENTS')}</div>
          <div className="aml-stat-val">{summary ? summary.segmentCount : '—'}</div>
        </div>
        <div className="aml-stat">
          <div className="aml-stat-label">{t('Aml.statOverdue', 'OVERDUE ITEMS')}</div>
          <div className={`aml-stat-val ${summary && (summary.filingsOverdue + summary.overdueReviewCount) > 0 ? 'aml-val-bad' : ''}`}>
            {summary ? summary.filingsOverdue + summary.overdueReviewCount : '—'}
          </div>
        </div>
      </div>

      <AmlNav frameworks={frameworks} frameworkId={frameworkId} onFrameworkChange={setFrameworkId} />

      {focusItems.length > 0 && (
        <>
          <div style={{ marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: '#334155' }}>
              {t('Aml.todaysFocus', "TODAY'S FOCUS")}
            </div>
            <div className="aml-muted" style={{ fontSize: '0.8125rem', marginTop: '0.125rem' }}>
              {t('Aml.todaysFocusSub', 'Items that move your readiness most when resolved.')}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            {focusItems.map((item, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #fdba74', borderLeft: '3px solid #ea580c', borderRadius: 8, padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                <div>
                  <span className={`aml-badge ${item.severity === 'HIGH' ? 'aml-pill-fail' : 'aml-pill-inprogress'}`}>{item.severity}</span>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, marginTop: '0.375rem' }}>{item.label}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', color: '#64748b' }}>{item.tag}</span>
                  <button className="aml-btn aml-btn-accent" style={{ height: 30, padding: '0 0.75rem', fontSize: '0.8125rem' }} onClick={() => navigate(item.to)}>
                    {t('Aml.open', 'View →')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="aml-section">
        <div className="aml-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{t('Aml.governance', 'Program governance')}</h2>
          {!showGovEdit && canManage && (
            <button className="aml-btn" style={{ height: 30, padding: '0 0.625rem', fontSize: '0.8125rem' }}
              onClick={() => openGovEdit(gov)}>
              {gov ? t('Aml.edit', 'Edit') : t('Aml.setUp', 'Set up')}
            </button>
          )}
        </div>

        {showGovEdit ? (
          <div style={{ padding: '1rem' }}>
            {govError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{govError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
              <label>
                <div className="aml-expand-label">{t('Aml.complianceOfficer', 'OFICIAL DE CUMPLIMIENTO')}</div>
                <input className="aml-input" style={{ width: '100%' }} value={govForm.complianceOfficerName}
                  onChange={(e) => setGovForm({ ...govForm, complianceOfficerName: e.target.value })} />
              </label>
              <label>
                <div className="aml-expand-label">{t('Aml.complianceOfficerEmail', 'Officer email (for framework-update alerts)')}</div>
                <input type="email" className="aml-input" style={{ width: '100%' }} value={govForm.complianceOfficerEmail}
                  onChange={(e) => setGovForm({ ...govForm, complianceOfficerEmail: e.target.value })} />
              </label>
              <label>
                <div className="aml-expand-label">{t('Aml.appointmentDate', 'Appointment date')}</div>
                <input type="date" className="aml-input" style={{ width: '100%' }} value={govForm.appointmentDate}
                  onChange={(e) => setGovForm({ ...govForm, appointmentDate: e.target.value })} />
              </label>
              <label>
                <div className="aml-expand-label">{t('Aml.registrationNumber', 'UAFE registration number')}</div>
                <input className="aml-input" style={{ width: '100%' }} value={govForm.regulatorRegistration}
                  onChange={(e) => setGovForm({ ...govForm, regulatorRegistration: e.target.value })} />
              </label>
              <label>
                <div className="aml-expand-label">{t('Aml.committeeSize', 'Committee members')}</div>
                <input type="number" min={0} className="aml-input" style={{ width: '100%' }} value={govForm.committeeMemberCount}
                  onChange={(e) => setGovForm({ ...govForm, committeeMemberCount: Number(e.target.value) })} />
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-end', gap: '0.375rem' }}>
                <input type="checkbox" checked={govForm.independenceCheck}
                  onChange={(e) => setGovForm({ ...govForm, independenceCheck: e.target.checked })} />
                <span style={{ fontSize: '0.8125rem' }}>{t('Aml.independenceCheck', 'Officer independence confirmed')}</span>
              </label>
              <label>
                <div className="aml-expand-label">{t('Aml.riskMatrixHigh', 'Risk matrix: HIGH threshold (score ≥)')}</div>
                <input type="number" min={1} max={25} className="aml-input" style={{ width: '100%' }} value={govForm.riskMatrixHighThreshold}
                  onChange={(e) => setGovForm({ ...govForm, riskMatrixHighThreshold: Number(e.target.value) })} />
              </label>
              <label>
                <div className="aml-expand-label">{t('Aml.riskMatrixMedium', 'Risk matrix: MEDIUM threshold (score ≥)')}</div>
                <input type="number" min={1} max={25} className="aml-input" style={{ width: '100%' }} value={govForm.riskMatrixMediumThreshold}
                  onChange={(e) => setGovForm({ ...govForm, riskMatrixMediumThreshold: Number(e.target.value) })} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.875rem' }}>
              <button className="aml-btn aml-btn-primary" disabled={govSaving} onClick={saveGovernance}>{t('Aml.save', 'Save')}</button>
              <button className="aml-btn" onClick={() => setShowGovEdit(false)}>{t('Aml.cancel', 'Cancel')}</button>
            </div>
          </div>
        ) : gov ? (
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
            <div>
              <div className="aml-expand-label">{t('Aml.complianceOfficer', 'OFICIAL DE CUMPLIMIENTO')}</div>
              <div>{gov.complianceOfficerName || t('Aml.notSet', 'Not set')}{gov.appointmentDate ? ` · ${t('Aml.appointed', 'appointed')} ${gov.appointmentDate}` : ''}</div>
              {gov.complianceOfficerEmail && <div className="aml-muted" style={{ fontSize: '0.8125rem' }}>{gov.complianceOfficerEmail}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('Aml.registeredWithUafe', 'Registered with UAFE')}</span>
              <span className={`aml-pill ${gov.regulatorRegistration ? 'aml-pill-pass' : 'aml-pill-fail'}`}>
                {gov.regulatorRegistration || t('Aml.notSet', 'Not set')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('Aml.committee', 'Comité de Cumplimiento')}</span>
              <span>{t('Aml.committeeMembers', '{{count}} members', { count: gov.committeeMemberCount || 0 })}</span>
            </div>
            {trainingAverage != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{t('Aml.annualTraining', 'Annual AML training')}</span>
                <span>
                  {t('Aml.trainingAverage', '{{pct}}% average completion · {{count}} courses', { pct: trainingAverage, count: coursesWithPct.length })}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('Aml.riskMatrixThresholds', 'Risk matrix thresholds')}</span>
              <span>
                {t('Aml.riskMatrixThresholdsVal', 'HIGH ≥ {{high}} · MEDIUM ≥ {{medium}}', {
                  high: gov.riskMatrixHighThreshold ?? 15, medium: gov.riskMatrixMediumThreshold ?? 8,
                })}
              </span>
            </div>
          </div>
        ) : (
          <div className="aml-empty" style={{ padding: '1rem' }}>
            {t('Aml.governanceNotSet', 'No governance details recorded yet — add the compliance officer, UAFE registration and committee size.')}
          </div>
        )}
      </div>

      <div className="aml-section" style={{ marginTop: '1rem' }}>
        <div className="aml-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{t('Aml.training', 'Training')}</h2>
          {canManage && (
            <button className="aml-btn" style={{ height: 30, padding: '0 0.625rem', fontSize: '0.8125rem' }}
              onClick={() => setShowTrainingAdd((s) => !s)}>{t('Aml.addCourse', '+ Add course')}</button>
          )}
        </div>

        {showTrainingAdd && (
          <div style={{ padding: '1rem' }}>
            {trainingError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{trainingError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.625rem' }}>
              <input className="aml-input" placeholder={t('Aml.courseName', 'Course name')}
                value={trainingForm.courseName} onChange={(e) => setTrainingForm({ ...trainingForm, courseName: e.target.value })} />
              <input className="aml-input" placeholder={t('Aml.audienceRole', 'Audience role')}
                value={trainingForm.audienceRole} onChange={(e) => setTrainingForm({ ...trainingForm, audienceRole: e.target.value })} />
              <input className="aml-input" placeholder={t('Aml.period', 'Period (e.g. 2026 Q4)')}
                value={trainingForm.period} onChange={(e) => setTrainingForm({ ...trainingForm, period: e.target.value })} />
              <input type="number" min={0} max={100} className="aml-input" placeholder={t('Aml.completionPct', 'Completion %')}
                disabled={trainingForm.autoComputed}
                value={trainingForm.autoComputed ? '' : trainingForm.completionPct}
                onChange={(e) => setTrainingForm({ ...trainingForm, completionPct: e.target.value })} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.625rem', fontSize: '0.8125rem' }}>
              <input type="checkbox" checked={trainingForm.autoComputed}
                onChange={(e) => setTrainingForm({ ...trainingForm, autoComputed: e.target.checked })} />
              <span>{t('Aml.autoComputeTraining', 'Count completion from People training assignments (matched by course name)')}</span>
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.625rem' }}>
              <button className="aml-btn aml-btn-primary" disabled={trainingSaving} onClick={submitTraining}>{t('Aml.save', 'Save')}</button>
              <button className="aml-btn" onClick={() => setShowTrainingAdd(false)}>{t('Aml.cancel', 'Cancel')}</button>
            </div>
          </div>
        )}

        {training.length === 0 ? (
          <div className="aml-empty" style={{ padding: '1rem' }}>
            {t('Aml.noTraining', 'No training courses tracked yet.')}
          </div>
        ) : (
          <div className="aml-table-wrap">
            <table className="aml-table">
              <thead><tr>
                <th>{t('Aml.courseCol', 'COURSE')}</th><th>{t('Aml.audienceCol', 'AUDIENCE')}</th>
                <th>{t('Aml.periodCol', 'PERIOD')}</th><th>{t('Aml.completionCol', 'COMPLETION')}</th>
              </tr></thead>
              <tbody>
                {training.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.courseName}</td>
                    <td>{r.audienceRole || '—'}</td>
                    <td>{r.period || '—'}</td>
                    <td>
                      {r.completionPct != null ? (
                        <span className={`aml-pill ${r.completionPct >= 100 ? 'aml-pill-pass' : 'aml-pill-muted'}`}>{r.completionPct}%</span>
                      ) : r.autoComputed ? (
                        <span className="aml-muted" style={{ fontSize: '0.75rem' }}>{t('Aml.noMatchedAssignments', 'No matching assignments yet')}</span>
                      ) : '—'}
                      {r.autoComputed && r.assignmentsTotal > 0 && (
                        <span className="aml-muted" style={{ fontSize: '0.6875rem', marginLeft: '0.375rem' }}
                          title={t('Aml.autoComputedNote', 'Counted from People training assignments')}>
                          {t('Aml.autoCountedFrom', 'Auto · {{done}}/{{total}}', { done: r.assignmentsCompleted, total: r.assignmentsTotal })}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
